import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import {
  applyPropLocalizedTranslations,
  selectLocalizedLanguages,
  selectPropLocalizedEntries,
} from "../../app/slices/TrainingLocalizationSlice";
import { selectReferenceLanguage } from "../../app/slices/TrainingSlice";
import { TranslationService } from "../../services/TranslationService";
import {
  TRANSLATION_FAILED_KEY,
  TRANSLATION_FAILURE_TOAST_MS,
} from "../../helpers/translationFeedback";

export default function usePropLocalizedTranslation(localizedKeys: string[]) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = useSelector(selectToken);
  const referenceLanguage = useSelector(selectReferenceLanguage);
  const localizedLanguages = useSelector(selectLocalizedLanguages);
  const propLocalizedEntries = useSelector(selectPropLocalizedEntries);
  const [isTranslating, setIsTranslating] = useState(false);

  const keys = useMemo(
    () => Array.from(new Set(localizedKeys)),
    [localizedKeys],
  );
  const targetLanguages = useMemo(
    () =>
      localizedLanguages.filter(
        (language) => language.shortcode !== referenceLanguage,
      ),
    [localizedLanguages, referenceLanguage],
  );
  const canTranslate = Boolean(
    token && referenceLanguage && targetLanguages.length > 0 && keys.length > 0,
  );
  const hasMissingTranslations = targetLanguages.some((language) =>
    keys.some(
      (key) =>
        !propLocalizedEntries[key]?.[language.shortcode]?.value?.trim(),
    ),
  );

  const translate = useCallback(
    async (missingOnly: boolean) => {
      if (!canTranslate || isTranslating) return;

      setIsTranslating(true);
      try {
        const translatedBatches = await Promise.all(
          targetLanguages.map(async (language) => {
            const keysToTranslate = missingOnly
              ? keys.filter(
                  (key) =>
                    !propLocalizedEntries[key]?.[
                      language.shortcode
                    ]?.value?.trim(),
                )
              : keys;
            if (keysToTranslate.length === 0) return [];

            const sourceTexts = keysToTranslate.map(
              (key) =>
                propLocalizedEntries[key]?.[referenceLanguage]?.value ?? "",
            );
            const translatedValues = await TranslationService.TranslateTexts(
              token,
              sourceTexts,
              referenceLanguage,
              language.shortcode,
            );

            return keysToTranslate.flatMap((key, index) => {
              const value = translatedValues[index];
              return typeof value === "string"
                ? [{ key, shortcode: language.shortcode, value }]
                : [];
            });
          }),
        );
        const translations = translatedBatches.flat();

        if (translations.length > 0) {
          dispatch(applyPropLocalizedTranslations({ translations }));
        }
      } catch (error) {
        // The service used to resolve with an empty array on failure, which the
        // `typeof value === "string"` filter below turned into a silent no-op.
        // It rejects now, so say so instead of appearing to have worked.
        toast.error(t(TRANSLATION_FAILED_KEY) as string, {
          position: "top-right",
          autoClose: TRANSLATION_FAILURE_TOAST_MS,
        });
      } finally {
        setIsTranslating(false);
      }
    },
    [
      canTranslate,
      dispatch,
      isTranslating,
      keys,
      propLocalizedEntries,
      referenceLanguage,
      t,
      targetLanguages,
      token,
    ],
  );

  return {
    canTranslateAll: canTranslate,
    canTranslateMissing: canTranslate && hasMissingTranslations,
    isTranslating,
    translateAll: () => translate(false),
    translateMissing: () => translate(true),
  } as const;
}
